<template>
  <admin-page-shell :title="$t('frontend_groovemail_ip_block_title')">
    <server-table
      class="text-center groovemail-ip-block-table"
      ref="serverTable"
      :url="blockedIPsListingUrl"
      tableRef="ipsTable"
      :slotData="ipBlockSlotData"
      :tableColumns="ipsTableColumns"
      :tableOptions="ipsTableOptions"
      tableTitle="frontend_groovemail_ip_block_title"
      @loaded="hideLoader()"
      @loading="showLoader()"
    >
    </server-table>

    <!-- Confirmation delete modal -->
    <confirm-modal
      :ref="`confirmation-unblock-ip`"
      :name="`confirmation-unblock-ip`"
      :buttonText="$t('frontend_confirm')"
      :cancelText="$t('frontend_cancel')"
      :confirmMessage="this.$t('frontend_bouses_delete_bonus_title')"
      :messageContent="this.$t('frontend_groovemail_ip_block_are_you_sure')"
      @confirm="unblockIpAddress"
      @close="closeUnBlockIpModal"
    />
  </admin-page-shell>
</template>

<script>
import axios from 'axios';
import { mapActions } from 'vuex';
import ServerTable from '@groovepages/gd-ui-kit/components/tables/ServerTable.vue';
import AdminPageShell from '../AdminPageShell.vue';
import monthsMixin from '@/mixin/monthsMixin';
import { postData } from '@/utils/apiHelper.js';
import { adminUrls } from '@/utils/urls.js';
import demoModeValuesMixin from '@/mixin/demoModeValuesMixin.js';
import { showRequestErrorAlert } from '@/components/showDialog.js';

let self;

export default {
  middleware: 'auth',
  metaInfo() {
    return { title: this.$t('frontend_groovemail_ip_block_title') };
  },

  mixins: [demoModeValuesMixin, monthsMixin],

  data() {
    return {
      blockedIPsListingUrl: adminUrls.ipBlockUrl.blockedIPListing,
      confirmId: null,
      ipsTableColumns: ['ip_address', 'created_at', 'actions'],
      ipsTableOptions: {
        perPage: 10,
        debounce: 1000,
        alwaysShowPerPageSelect: true,
        perPageValues: [10, 25, 50, 100],
        pagination: { chunk: 5 },
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        texts: {
          filterPlaceholder: this.$t('frontend_groovemail_tables_search_query'),
        },
        sortable: ['ip_address', 'created_at'],
        filterable: ['ip_address', 'created_at'],
        headings: {
          ip_address: () =>
            this.$t('frontend_groovemail_contacts_step2_ip_address'),
          created_at: () => this.$t('frontend_groovemail_created_at'),
          actions: () => this.$t('frontend_groovemail_mail_lead_actions'),
        },
        requestFunction(data) {
          const requestParams = { ...data, ascending: 1 - data.ascending };
          const self = this;
          return axios
            .get(self.url, {
              params: requestParams,
            })
            .catch((e) => {
              self.dispatch('error', e);
              self.hideLoader();
            });
        },
        responseAdapter({ data }) {
          if(self.demoMode) {
            const newData = data?.data.map((obj) => ({
              ...obj,
              ip_address: self.basicIpAddressText,
            }));

            return {
              data: newData,
              count: newData.length,
            };
          }
          return {
            data: data.data,
            count: data.total,
          };
        },
      },
    };
  },

  created() {
    self = this;
  },

  watch: {
    // Used to refresh ip table when demo mode changes
    demoMode() {
      this.$refs.serverTable.$refs.ipsTable.refresh();
    },
  },

  computed: {
    // Used to return demoMode value from store.
    demoMode() {
      return this.$store.state.demoMode.isDemoMode;
    },

    /**
     *  Returns slot data for ip block table.
     *  @return {array} details for slot columns for ip block table.
     */
    ipBlockSlotData() {
      return [
        {
          slotName: 'ip_address',
          slotType: 'computed',
          classes: () => {
            return '';
          },
          value: (props) => {
            return props.row.ip_address
              ? props.row.ip_address
              : this.$t('frontend_groovemail_not_available');
          },
        },
        {
          slotName: 'created_at',
          slotType: 'computed',
          classes: () => {
            return '';
          },
          value: (props) => {
            return props.row.created_at
              ? `${new Date(props.row.created_at).getDate()} ${
                  this.months[new Date(props.row.created_at).getMonth()]
                }, ${new Date(props.row.created_at).getFullYear()}`
              : this.$t('frontend_groovemail_not_available');
          },
        },
        {
          slotName: 'actions',
          slotType: 'actions',
          conditions: () => {
            return true;
          },
          classes: () => {
            return {
              outerDiv: '',
              dropdown: '',
              outerSpan: '',
            };
          },
          actionList: [
            {
              id: 0,
              name: () => this.$t('frontend_groovemail_ip_block_safe_to_send'),
              conditions: () => {
                return true;
              },
              callback: (props) => this.openUnBlockIpModal(props.row.id),
            },
          ],
        },
      ];
    },
  },

  components: {
    AdminPageShell,
    ServerTable,
  },

  methods: {
    ...mapActions('loader', ['LOADING']),

    // show loader
    showLoader() {
      this.LOADING(true);
    },

    // hide loader
    hideLoader (){
      this.LOADING(false);
    },


    // open unBlockIp modal
    async openUnBlockIpModal(id) {
      // set the Confirm id
      this.confirmId = id;

      // open the modal
      this.$refs[`confirmation-unblock-ip`].open();
    },

    // close unBlockIp modal
    closeUnBlockIpModal() {
      // reset the Confirm id
      this.confirmId = null;
    },


    // unblock ip address
    async unblockIpAddress() {
      try {
        await postData(adminUrls.ipBlockUrl.unblockIP, { id: this.confirmId });
        this.$refs.serverTable.updateData(
          ipData?.data?.data,
          ipData?.data?.total
        );

      } catch (error) {
        // show error alert
        showRequestErrorAlert(error?.response?.data?.error);
      }
    },
  },
};
</script>

<style lang="scss">
.groovemail-ip-block-table #gd-server-table-view {
  .VueTables__table th:last-child span {
    visibility: visible;
  }

  .VueTables__search-field label {
    display: none;
  }

  .w-20 {
    width: 7rem;
  }

  .ip_address_btn {
    width: auto;
  }
}
</style>
